import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, MailCheck, AlertTriangle, LogIn } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // ~60s max wait — Stripe is usually <5s

/**
 * Customer-facing post-payment landing page.
 * Polls /api/checkout/status/{session_id} until the backend confirms
 * Stripe marked the session paid AND the post-payment side effects
 * (client account creation + credentials email) completed.
 *
 * The polling is the ONLY way our backend learns about completion in
 * this preview (Stripe webhook delivery to a preview URL is unreliable)
 * so this poller is mandatory per the Stripe playbook.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState({
    loading: true,
    paid: false,
    handled: false,
    amount: null,
    currency: 'usd',
    error: null,
    attempts: 0,
  });
  const pollTimeout = useRef(null);
  const cancelled = useRef(false);

  const poll = useCallback(async (attempt) => {
    if (cancelled.current) return;
    try {
      const res = await axios.get(`${API}/api/checkout/status/${encodeURIComponent(sessionId)}`);
      const d = res.data || {};
      const paid = d.payment_status === 'paid';
      const handled = !!d.post_payment_handled;
      setState((prev) => ({
        ...prev,
        loading: !paid || !handled,
        paid,
        handled,
        amount: typeof d.amount_total === 'number' ? d.amount_total / 100 : prev.amount,
        currency: d.currency || prev.currency,
        attempts: attempt,
        error: null,
      }));
      if (paid && handled) return; // done
      if (attempt >= MAX_POLL_ATTEMPTS) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: paid
            ? 'Payment received — account is still being set up. Please check your email shortly.'
            : 'Payment is still processing. We will email you once Stripe confirms it.',
        }));
        return;
      }
      pollTimeout.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    } catch (e) {
      if (attempt >= MAX_POLL_ATTEMPTS) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.response?.data?.detail || 'Unable to verify payment right now. Please check your email.',
        }));
        return;
      }
      pollTimeout.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, loading: false, error: 'Missing session_id in URL.' }));
      return;
    }
    poll(0);
    return () => {
      cancelled.current = true;
      if (pollTimeout.current) clearTimeout(pollTimeout.current);
    };
  }, [poll, sessionId]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-slate-50 via-purple-50 to-emerald-50"
      data-testid="payment-success-page"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-emerald-600 text-white px-7 py-5">
          <h1 className="text-xl font-semibold">Local Flyer Delivery</h1>
          <p className="text-xs text-white/80 mt-1">Payment confirmation</p>
        </div>

        <div className="px-7 py-8 text-center">
          {state.loading && !state.error && (
            <div data-testid="payment-loading">
              <Loader2 size={48} className="mx-auto text-purple-600 animate-spin" />
              <p className="mt-4 font-semibold text-gray-900">Confirming your payment…</p>
              <p className="text-sm text-gray-500 mt-1">
                This typically takes 2-5 seconds.
              </p>
            </div>
          )}

          {!state.loading && state.paid && state.handled && (
            <div data-testid="payment-confirmed">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 size={36} className="text-emerald-600" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-900">Payment received</h2>
              {state.amount && (
                <p className="text-lg text-emerald-700 font-semibold mt-1">
                  ${state.amount.toFixed(2)} {String(state.currency).toUpperCase()}
                </p>
              )}
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-left">
                <p className="font-semibold text-emerald-900 flex items-center gap-1.5 text-sm">
                  <MailCheck size={16} /> Check your inbox
                </p>
                <p className="text-sm text-emerald-800 mt-1.5 leading-relaxed">
                  We just emailed you your <strong>login credentials</strong> for the
                  Client Delivery Portal — you can sign in to track delivery progress
                  and view your reports.
                </p>
              </div>
              <a
                href="/client/login"
                className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm transition-colors"
                data-testid="payment-success-portal-link"
              >
                <LogIn size={16} /> Go to Client Portal
              </a>
            </div>
          )}

          {!state.loading && state.error && (
            <div data-testid="payment-error">
              <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={36} className="text-amber-600" />
              </div>
              <h2 className="mt-5 text-xl font-semibold text-gray-900">Still working on it…</h2>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{state.error}</p>
              <p className="text-xs text-gray-500 mt-4">
                Need help? Email{' '}
                <a href="mailto:info@localflyerdelivery.com" className="text-purple-700 underline">
                  info@localflyerdelivery.com
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
